import React, { useEffect, useState } from "react";
import { Star, ExternalLink, Quote } from "lucide-react";
import api from "../lib/api";

export const GOOGLE_REVIEWS_URL = "https://www.google.com/maps?cid=14493707639117920646";

export function useGoogleReviews() {
  const [google, setGoogle] = useState({ enabled: false, reviews: [], write_review_url: GOOGLE_REVIEWS_URL, maps_url: GOOGLE_REVIEWS_URL });
  useEffect(() => {
    api.get("/google-reviews").then(({ data }) => setGoogle(data)).catch(() => {});
  }, []);
  return google;
}

export function Stars({ rating, size = "w-4 h-4" }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= rating ? "fill-[#FF2079] text-[#FF2079]" : "text-white/20"}`} />
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const meta = [review.source === "google" ? "Google review" : review.service, review.date].filter(Boolean).join(" · ");
  return (
    <div data-testid={`review-card-${review.id}`} className="glass-card rounded-sm p-7 flex flex-col">
      <Quote className="w-6 h-6 text-[#FF2079]/60 mb-4" />
      <p className="text-[#E8E4EA] leading-relaxed flex-1 line-clamp-[8]">{review.text}</p>
      <div className="mt-6 flex items-center justify-between gap-3">
        <div>
          {review.author_uri ? (
            <a href={review.author_uri} target="_blank" rel="noreferrer" className="text-white font-medium hover:text-[#FF2079]">{review.name}</a>
          ) : (
            <div className="text-white font-medium">{review.name}</div>
          )}
          <div className="text-xs text-[#A09CA3]">{meta}</div>
        </div>
        <Stars rating={review.rating} />
      </div>
    </div>
  );
}

function ReviewButtons({ google }) {
  return (
    <div className="flex flex-wrap gap-3 self-start md:self-auto">
      <a href={google.maps_url} target="_blank" rel="noreferrer" data-testid="google-reviews-link"
        className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF2079] text-white rounded-sm h-11 px-6 text-[10px] tracking-[0.28em] uppercase transition-colors">
        See us on Google <ExternalLink className="w-3.5 h-3.5" />
      </a>
      <a href={google.write_review_url} target="_blank" rel="noreferrer" data-testid="write-review-link"
        className="pink-glow-btn inline-flex items-center bg-[#FF2079] hover:bg-[#D91666] text-white rounded-sm h-11 px-6 text-[10px] tracking-[0.28em] uppercase transition-colors">
        Write a Review
      </a>
    </div>
  );
}

export default function Reviews() {
  const [own, setOwn] = useState([]);
  const google = useGoogleReviews();

  useEffect(() => {
    api.get("/reviews").then(({ data }) => setOwn(data)).catch(() => {});
  }, []);

  const reviews = [...google.reviews, ...own];
  const avg = google.rating || (reviews.length ? reviews.reduce((a, r) => a + r.rating, 0) / reviews.length : 0);
  const count = google.user_rating_count || reviews.length;

  return (
    <section id="reviews" data-testid="reviews-section" className="relative py-24 md:py-28 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="eyebrow mb-4">Client Love</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Kind words from <span className="italic text-[#FF2079]">our guests.</span>
            </h2>
            {count > 0 && (
              <div data-testid="reviews-average" className="mt-4 flex items-center gap-3 text-[#A09CA3] text-sm">
                <Stars rating={Math.round(avg)} /> {avg.toFixed(1)} from {count} review{count > 1 ? "s" : ""}{google.enabled ? " on Google" : ""}
              </div>
            )}
          </div>
          <ReviewButtons google={google} />
        </div>
        {reviews.length > 0 && (
          <div className="grid md:grid-cols-3 gap-5">
            {reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
          </div>
        )}
        {reviews.length === 0 && (
          <p data-testid="reviews-empty" className="text-[#A09CA3]">
            Visited us recently? We'd love to hear about your glow — leave us a review on Google.
          </p>
        )}
        {google.enabled && (
          <p data-testid="google-attribution" className="mt-6 text-xs text-[#A09CA3]">Google reviews provided by Google.</p>
        )}
      </div>
    </section>
  );
}
